import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';

const fmt = (n) => n != null ? '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtFull = (n) => n != null ? '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

function trend(curr, prev) {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
}

function TrendBadge({ pct }) {
  if (pct == null) return null;
  const up = pct > 0;
  return (
    <span className={`text-[8px] font-semibold ml-1 ${up ? 'text-red-500' : 'text-emerald-600'}`}>
      {up ? '▲' : '▼'}{Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export default function POSpendPage() {
  const [granularity, setGranularity] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('purchase-orders/analytics', { granularity })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [granularity]);

  const periods = data?.periods || [];
  const bySupplier = data?.by_supplier || [];
  // POs raised in a supplier's currency are not converted, so they're left out of
  // these sterling figures and listed here instead of being silently dropped.
  const foreign = data?.foreign_currency || [];

  // Latest two periods for summary
  const latest = periods[0];
  const previous = periods[1];

  const latestTotal = latest?.total_spend || 0;
  const latestOverhead = latest?.overhead_spend || 0;
  const latestSow = latest?.sow_spend || 0;

  const totalSpend12m = periods.slice(0, granularity === 'monthly' ? 12 : granularity === 'quarterly' ? 4 : 1)
    .reduce((s, p) => s + (p.total_spend || 0), 0);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">PO Spend Dashboard</h2>
          <div className="text-[9px] text-gray-500">Purchase order spend analysis — overhead vs SOW-linked · all figures in GBP</div>
        </div>
        <div className="flex gap-1">
          {[['monthly','Monthly'],['quarterly','Quarterly'],['yearly','Yearly']].map(([id, label]) => (
            <button key={id} onClick={() => setGranularity(id)}
              className={`px-3 py-1.5 text-[10px] font-semibold rounded border transition-colors ${granularity === id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {foreign.length > 0 && (
        <div className="mb-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-[9px] text-amber-800">
          <span className="font-bold">Excludes foreign-currency POs.</span>{' '}
          {foreign.map(f => `${f.po_count} PO${f.po_count !== 1 ? 's' : ''} totalling ${Number(f.total_spend || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${f.currency}`).join(' · ')}.
          {' '}These are not converted to GBP, so they are left out of the figures below.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">
            {granularity === 'monthly' ? 'This Month' : granularity === 'quarterly' ? 'This Quarter' : 'This Year'} Spend
          </div>
          <div className="text-xl font-extrabold text-navy">{fmt(latestTotal)}</div>
          {latest && previous && (
            <div className="text-[8px] text-gray-400 mt-0.5">
              prev {fmt(previous.total_spend)}
              <TrendBadge pct={trend(latestTotal, previous.total_spend)} />
            </div>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">SOW-Linked</div>
          <div className="text-xl font-extrabold text-emerald-700">{fmt(latestSow)}</div>
          {latestTotal > 0 && <div className="text-[8px] text-gray-400 mt-0.5">{((latestSow/latestTotal)*100).toFixed(0)}% of spend</div>}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">Overhead (No SOW)</div>
          <div className="text-xl font-extrabold text-amber-600">{fmt(latestOverhead)}</div>
          {latestTotal > 0 && <div className="text-[8px] text-gray-400 mt-0.5">{((latestOverhead/latestTotal)*100).toFixed(0)}% of spend</div>}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">
            {granularity === 'monthly' ? 'Rolling 12m' : granularity === 'quarterly' ? 'Rolling 4q' : 'All time'} Total
          </div>
          <div className="text-xl font-extrabold text-navy">{fmt(totalSpend12m)}</div>
          <div className="text-[8px] text-gray-400 mt-0.5">{periods.length} periods</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Period breakdown table */}
        <div className="col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-[10px] font-bold text-navy">Spend by Period</span>
            <span className="text-[8px] text-gray-400">{periods.length} periods shown</span>
          </div>
          {loading ? (
            <div className="p-6 text-center text-[10px] text-gray-400">Loading…</div>
          ) : periods.length === 0 ? (
            <div className="p-6 text-center text-[10px] text-gray-400">No purchase order data available</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-400 uppercase">Period</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">POs</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">SOW-linked</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Overhead</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Total</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">vs prev</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => {
                  const prev = periods[i + 1];
                  const chg = trend(p.total_spend, prev?.total_spend);
                  const maxSpend = Math.max(...periods.map(x => x.total_spend || 0));
                  const barW = maxSpend > 0 ? ((p.total_spend || 0) / maxSpend) * 100 : 0;
                  return (
                    <tr key={p.period} className={`border-b border-gray-50 ${i === 0 ? 'bg-navy/3' : 'hover:bg-gray-50/50'}`}>
                      <td className="px-3 py-2">
                        <div className="text-[10px] font-semibold text-gray-800">{p.period}</div>
                        <div className="h-1 bg-gray-100 rounded mt-1 w-20">
                          <div className="h-1 bg-navy/60 rounded" style={{ width: `${barW}%` }} />
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-[9px] text-gray-500">{p.po_count}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-[9px] font-semibold text-emerald-700">{fmtFull(p.sow_spend)}</div>
                        {p.sow_count > 0 && <div className="text-[7px] text-gray-400">{p.sow_count} POs</div>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-[9px] font-semibold text-amber-600">{fmtFull(p.overhead_spend)}</div>
                        {p.overhead_count > 0 && <div className="text-[7px] text-gray-400">{p.overhead_count} POs</div>}
                      </td>
                      <td className="px-3 py-2 text-right text-[10px] font-bold text-navy">{fmtFull(p.total_spend)}</td>
                      <td className="px-2 py-2 text-right">
                        <TrendBadge pct={chg} />
                        {chg == null && <span className="text-[8px] text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Top suppliers */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="text-[10px] font-bold text-navy">Top Suppliers (12m)</span>
          </div>
          {bySupplier.length === 0 ? (
            <div className="p-4 text-center text-[10px] text-gray-400">No data</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {bySupplier.map((s, i) => {
                const maxS = bySupplier[0]?.total_spend || 1;
                return (
                  <div key={s.supplier_name} className="px-3 py-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[8px] text-gray-300 w-4">{i + 1}</span>
                        <span className="text-[9px] font-semibold text-gray-700 truncate max-w-[110px]">{s.supplier_name}</span>
                      </div>
                      <span className="text-[9px] font-bold text-navy">{fmt(s.total_spend)}</span>
                    </div>
                    <div className="h-1 bg-gray-100 rounded ml-6">
                      <div className="h-1 bg-amber-400 rounded" style={{ width: `${(s.total_spend / maxS) * 100}%` }} />
                    </div>
                    <div className="text-[7px] text-gray-400 ml-6 mt-0.5">{s.po_count} PO{s.po_count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
